/**
 * TimeManager - Manages game time and day/night cycle
 */

export class TimeManager {
  private gameTime: number = 8; // Start at 8:00 AM
  private day: number = 1;
  private dayLength: number; // Real seconds per game day
  private timeScale: number = 1;
  private paused: boolean = false;
  private totalPlayTime: number = 0;

  // Callbacks
  public onDayStart: ((day: number) => void) | null = null;
  public onNightStart: ((day: number) => void) | null = null;
  public onHourChange: ((hour: number) => void) | null = null;

  private lastHour: number = 8;
  private wasDay: boolean = true;

  constructor(dayLength: number = 600) {
    this.dayLength = dayLength; // Default 10 minutes per day
  }

  update(deltaTime: number): void {
    if (this.paused) return;

    this.totalPlayTime += deltaTime;

    // Convert real time to game time
    const hoursPerSecond = 24 / this.dayLength;
    this.gameTime += deltaTime * hoursPerSecond * this.timeScale;

    // Check for day rollover
    if (this.gameTime >= 24) {
      this.gameTime -= 24;
      this.day += 1;
      this.onDayStart?.(this.day);
    }

    // Check for hour change
    const currentHour = Math.floor(this.gameTime);
    if (currentHour !== this.lastHour) {
      this.lastHour = currentHour;
      this.onHourChange?.(currentHour);
    }

    // Check for day/night transition
    const isDay = this.isDay();
    if (isDay !== this.wasDay) {
      this.wasDay = isDay;
      if (isDay) {
        this.onDayStart?.(this.day);
      } else {
        this.onNightStart?.(this.day);
      }
    }
  }

  /** Get current game hour (0-24) */
  getTime(): number {
    return this.gameTime;
  }

  /** Get formatted time string (HH:MM) */
  getTimeFormatted(): string {
    const hours = Math.floor(this.gameTime);
    const minutes = Math.floor((this.gameTime - hours) * 60);
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  }

  /** Get current day number */
  getDay(): number {
    return this.day;
  }

  /** Is it daytime? (6:00 - 20:00) */
  isDay(): boolean {
    return this.gameTime >= 6 && this.gameTime < 20;
  }

  /** Is it nighttime? (20:00 - 6:00) */
  isNight(): boolean {
    return !this.isDay();
  }

  /** Get daylight intensity (0 = dark, 1 = bright) */
  getDaylightIntensity(): number {
    const hour = this.gameTime;

    // Night (20:00 - 4:00): dark
    if (hour >= 20 || hour < 4) {
      return 0.2;
    }

    // Dawn (4:00 - 6:00): brightening
    if (hour >= 4 && hour < 6) {
      return 0.2 + ((hour - 4) / 2) * 0.8;
    }

    // Day (6:00 - 18:00): bright
    if (hour >= 6 && hour < 18) {
      return 1.0;
    }

    // Dusk (18:00 - 20:00): dimming
    if (hour >= 18 && hour < 20) {
      return 1.0 - ((hour - 18) / 2) * 0.8;
    }

    return 1.0;
  }

  /** Set time scale (1 = normal, 2 = double speed) */
  setTimeScale(scale: number): void {
    this.timeScale = Math.max(0, scale);
  }

  getTimeScale(): number {
    return this.timeScale;
  }

  /** Get day length in real seconds */
  getDayLength(): number {
    return this.dayLength;
  }

  /** Pause time */
  pause(): void {
    this.paused = true;
  }

  /** Resume time */
  resume(): void {
    this.paused = false;
  }

  isPaused(): boolean {
    return this.paused;
  }

  /** Skip to specific hour */
  skipToTime(hour: number): void {
    const targetHour = hour % 24;
    if (targetHour < this.gameTime) {
      // Skip to next day
      this.day += 1;
    }
    this.gameTime = targetHour;
  }

  /** Skip to dawn (6:00) */
  skipToDawn(): void {
    if (this.gameTime >= 6) {
      this.day += 1;
    }
    this.gameTime = 6;
    this.onDayStart?.(this.day);
  }

  /** Get total play time in seconds */
  getTotalPlayTime(): number {
    return this.totalPlayTime;
  }

  /** Format play time as HH:MM:SS */
  getPlayTimeFormatted(): string {
    const hours = Math.floor(this.totalPlayTime / 3600);
    const minutes = Math.floor((this.totalPlayTime % 3600) / 60);
    const seconds = Math.floor(this.totalPlayTime % 60);
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }

  /** Set state for loading */
  setState(gameTime: number, day: number, totalPlayTime: number): void {
    this.gameTime = gameTime;
    this.day = day;
    this.totalPlayTime = totalPlayTime;
    this.lastHour = Math.floor(gameTime);
    this.wasDay = this.isDay();
  }
}
